/* eslint-disable no-param-reassign */
import { NavigateFunction, useNavigate, useSearchParams } from "react-router-dom";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  UIEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Theme,
  CircularProgress,
  Switch,
  Grid,
} from "@mui/material";
import { LocationContext, SuccessAlertContext } from "App";
import { Search } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpIcon from "@mui/icons-material/Help";
import Notify from "config/notify";
import ERROR_MESSAGE from "constants/message";
import { checkAccessTokenExpired, getUserType } from "util/auth";
import { Severity } from "type";
import { AxiosError } from "axios";
import useOrderList from "../hooks/useOrderList";
import OrderContainer from "../components/OrderContainer";
import OrderTable from "../components/OrderTable";
import OrderSearchDialog from "../components/OrderSearchDialog";
import useOrderSearch from "../hooks/useOrderSearch";
import {
  OrderNumberStatus,
  OrderReceiptStatus,
  OrderSupplier,
  OrderSupplierCsv,
  OrderSupplierUpdateParams,
  ReceiptNumberStatus,
  ReceiveMode,
  SearchSupplierApiParams,
  ShippingStatus,
} from "../types";
import {
  convertUTCToDate,
  extractNoDupplicationSupplier,
  getReceivedStatusCandidates,
  getReceivedStatusParam,
  localeStringDisZero,
  orderStatusToNumber,
  removeEmptyValues,
  shippingStatusStrToNumber,
  toOrderCheck,
} from "../utils/common";
import {
  MultiTypography,
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellNarrow,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "../components/TableCellSmall";
import { localEnv, hasRepresentativeCd, orderReceiptStatusList, RECEIVE_MODE } from "../constants";
import CsvModals from "../components/CsvModals";
import OrderConfirmDialog from "../components/OrderConfirmDialog";
import Surround from "../components/Surround";
import useOrderSupplierList from "../hooks/useOrderSupplierList";
import downloadOrderSupplierCsv from "../api/downloadOrderSupplierCsv";
import updateOrderSupplierApi from "../api/updateOrderSupplierApi";
import OrderSupplierConfirmDialog from "../components/OrderSupplierConfirmDialog";
import ShipInfo from "../components/ShipInfo";
import SelectDownloadMode from "../components/SelectDownloadMode";
import ReceiveEmailSetting from "../components/ReceiveEmailSetting";

const limit = 10;
const styles: { [key: string]: SxProps } = {
  buttonContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    position: "relative",
  },
  leftButtonContainer: {
    position: "absolute",
    top: 0,
    left: 0,
  },
  activeButton: {
    backgroundColor: "secondary.main",
    color: "var(--white)",
    "&:hover": {
      backgroundColor: "secondary.light",
    },
  },
  supplierTopCol: {
    // textAlign: 'center',
    p: 0.5,
    borderBottom: "none",
    height: 58,
  },
  supplierBottomCol: {
    // textAlign: 'center',
    p: 0.5,
    top: 67,
  },
};

/**
 * ヘッダーを返す
 * @param selectedReceivedMode 選択されている受注モードボタン
 * @param onSearch 検索し直し
 * @param onBack 戻る
 * @param isNoSelectedOrder 一つも注文が選ばれていなければtrue
 * @param isRefineSearched 絞込検索がされているかどうか
 * @param setIsOrderSearch 検索モーダルを開閉
 * @param setIsOpenShipping 出荷情報設定モーダル開閉
 * @param setIsUploadOrderSupplierCsv 出荷CSVアップロードモーダル開閉
 * @param setIsOpenDownload ダウンロードの際に受注タイプを選択するモーダルを開閉
 * @param setIsOpenEmailSetting メールアドレス設定モーダルを開閉
 * @param showAlert アラート表示
 * @returns コンポーネント
 */
function HeaderBox(
  selectedReceivedMode: ReceiveMode, // 受注の検索モードを管理する 0から順に未ダウンロード、ダウンロード済み、発注済み、全て表示
  onSearch: (mode: ReceiveMode) => void,
  onBack: Function,
  isNoSelectedOrder: boolean,
  isRefineSearched: boolean,
  setIsOrderSearch: Dispatch<SetStateAction<boolean>>,
  setIsOpenShipping: Dispatch<SetStateAction<boolean>>,
  setIsUploadOrderSupplierCsv: Dispatch<SetStateAction<boolean>>,
  setIsOpenDownload: Dispatch<SetStateAction<boolean>>,
  setIsOpenEmailSetting: Dispatch<SetStateAction<boolean>>,
  showAlert: (msg: string, severity?: Severity) => void,
) {
  const userType = getUserType();
  return (
    <>
      <Box sx={styles.buttonContainer}>
        <Box sx={styles.leftButtonContainer}>
          <Box mx={1}>
            <Button onClick={() => onBack()} variant="custom-outlined">
              <Box>＜ 戻る</Box>
            </Button>
          </Box>
        </Box>
        { userType === "C0004" &&
          <Box mx={1}>
            <Button onClick={() => setIsOpenEmailSetting(true)} variant="contained">
              <Box>メール受信設定</Box>
            </Button>
          </Box>
        }
        <Box mx={1}>
          <Button onClick={() => setIsOrderSearch(true)} variant="contained">
            <Search />
            <Box>絞込検索</Box>
          </Button>
        </Box>
        <Box mx={1}>
          <Button
            onClick={() => setIsOpenShipping(true)}
            variant="contained"
            disabled={isNoSelectedOrder}
          >
            <Box>出荷情報設定</Box>
          </Button>
        </Box>
      </Box>
      <Box marginTop={1} />
      <Box sx={styles.buttonContainer}>
        <Box mx={1}>
          <Button onClick={() => {
            // if (isRefineSearched) showAlert('絞込検索中はダウンロードが出来ません。', 'error')
            // else setIsOpenDownload(true)
            // 絞込検索には関係なくダウンロード可能（タブは制御）
            setIsOpenDownload(true)
          }} variant="contained" disabled={selectedReceivedMode === 2 || selectedReceivedMode === 9}>
            <Box>受注CSVダウンロード</Box>
          </Button>
        </Box>
        <Box mx={1}>
          <Button onClick={() => setIsUploadOrderSupplierCsv(true)} variant="contained">
            <Box>出荷CSVアップロード</Box>
          </Button>
        </Box>
      </Box>
      <Box sx={{ ...styles.buttonContainer, ...{ mb: -5 } }}>
        <Surround title="表示する受注を選択" color="gray">
          <Grid container spacing={0} padding={1}>
            <Box mx={1}>
              <Button
                onClick={() => onSearch(RECEIVE_MODE.notDownloaded)}
                variant={
                  selectedReceivedMode === RECEIVE_MODE.notDownloaded
                    ? "selected-gray-outlined"
                    : "gray-outlined"
                }
              >
                <Box>未ダウンロード</Box>
              </Button>
            </Box>
            <Box mx={1}>
              <Button
                onClick={() => onSearch(RECEIVE_MODE.downloaded)}
                variant={
                  selectedReceivedMode === RECEIVE_MODE.downloaded
                    ? "selected-gray-outlined"
                    : "gray-outlined"
                }
              >
                <Box>未出荷全て</Box>
              </Button>
            </Box>
            <Box mx={1}>
              <Button
                onClick={() => onSearch(RECEIVE_MODE.shipped)}
                variant={
                  selectedReceivedMode === RECEIVE_MODE.shipped
                    ? "selected-gray-outlined"
                    : "gray-outlined"
                }
              >
                <Box>出荷済み</Box>
              </Button>
            </Box>
            <Box mx={1}>
              <Button
                onClick={() => onSearch(RECEIVE_MODE.all)}
                variant={
                  selectedReceivedMode === RECEIVE_MODE.all
                    ? "selected-gray-outlined"
                    : "gray-outlined"
                }
              >
                <Box>全て</Box>
              </Button>
            </Box>
          </Grid>
        </Surround>
      </Box>
    </>
  );
}

/**
 * 仕入先・外部倉庫受注商品一覧
 * @returns JSX.Element
 */
export default function OrderReceive() {
  const [isOrderSearch, setIsOrderSearch] = useState(false);
  const [isPlaceOrder, setIsPlaceOrder] = useState(false);
  const [isUpdateProcessing, setIsUpdateProcessing] = useState(false);
  const [isUploadOrderSupplierCsv, setIsUploadOrderSupplierCsv] = useState(false);
  const [isOpenDownload, setIsOpenDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert } = useContext(SuccessAlertContext);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedReceivedMode, setSelectedReceivedMode] = useState<ReceiveMode>(9); // 受注の検索モードを管理する 0から順に未ダウンロード、ダウンロード済み、発注済み、全て表示
  const [isOpenEmailSetting, setIsOpenEmailSetting] = useState(false);

  // 出荷情報設定モーダルの変数
  const [isOpenShipping, setIsOpenShipping] = useState(false);
  const [shippingCompanyName, setShippingCompanyName] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [shippingDate, setShippingDate] = useState("");
  const [expectedShippingDate, setExpectedShippingDate] = useState("");
  const [shippingStatus, setShippingStatus] = useState<ShippingStatus>("");
  const [arrivalDate, setArrivalDate] = useState("");
  // 出荷情報設定日付エラー
  const [shippingDateError, setShippingDateError] = useState<string | null>(null);
  const [expectedShippingDateError, setExpectedShippingDateError] = useState<string | null>(null);

  // 検索条件のstate
  const [
    {
      isRepresentativeCd,
      isCustomerOrder,
      shopName,
      orderStatus,
      orderReceiptStatus,
      storeOrderer,
      approver,
      orderStartDate,
      orderEndDate,
      companyCdField,
      companyName,
      centerCd,
      orderNumber,
      productCd,
      productName,
      brand,
      janCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      storehouseSupplierWithBlank,
      storehouse,
      supplier,
      category,
    },
    {
      setIsRepresentativeCd,
      setIsCustomerOrder,
      setShopName,
      setOrderStatus,
      setOrderReceiptStatus,
      setStoreOrderer,
      setApprover,
      setOrderStartDate,
      setOrderEndDate,
      setCompanyCdField,
      setCompanyName,
      setCenterCd,
      setOrderNumber,
      setProductCd,
      setProductName,
      setBrand,
      setjanCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setClassification,
      setStorehouseSupplierWithBlank,
      setStorehouse,
      setSupplier,
      setCategory,
    },
    { classificationOptions, tyreOptions, wheelOptions, brandOptions },
    { isLoading: isOptionsLoading },
  ] = useOrderSearch(true, undefined, undefined, true);

  const [
    {
      total,
      orderList,
      isLoading: isOrderLoading,
      isMoreLoading,
      isFastLoading,
      fireMoreLoadEvent,
    },
    { setOrderList, get, getMore: getMoreOrderList, setFireMoreLoadEvent, setInit, setTotal },
  ] = useOrderSupplierList(showAlert);
  const navigate = useNavigate();

  const checkRefineSearch = () => {
    if (
      orderNumber   === "" &&
      productCd     === "" &&
      janCd         === "" &&
      productNumber === "" &&
      productName   === "" &&
      orderStatus   === "" &&
      shopName      === "" &&
      orderStartDate=== "" &&
      orderEndDate  === ""
    ) {
      return false;
    }
    return true;
  }

  // 現在の検索条件にあるデータを基にAPIのパラメータを作成
  const createGetListApiParams = (mode?: ReceiveMode, isDefaultStatus?: boolean): SearchSupplierApiParams => {
    const params: Omit<SearchSupplierApiParams, "limit" | "offset"> = {
      mode: mode ?? selectedReceivedMode,
      // sort_type: 2, // 注文日昇順
      // is_representative: hasRepresentativeCd.indexOf(isRepresentativeCd) === 2 ? 1 : 0, // APIリファレンスにはあるが画面にはないため不要
      order_status: getReceivedStatusParam(mode ?? selectedReceivedMode, isDefaultStatus ? "" : orderReceiptStatus),
      store_name: shopName,
      order_no: orderNumber,
      // order_build_no: orderNumber,
      item_cd: productCd,
      item_name: productName,
      jan_cd: janCd,
      item_num: productNumber,
      order_date_start: convertUTCToDate(orderStartDate),
      order_date_end: convertUTCToDate(orderEndDate),
    };
    return removeEmptyValues(params);
  };

  const handleSearch = async (mode?: ReceiveMode) => {
    setIsOrderSearch(false);
    // モードを変更した場合にデフォルトのステータスを使用するので、含んでいれば使用しないフラグ
    let includeFlag = false;
    let params: SearchSupplierApiParams;
    if (mode !== undefined) {
      setSelectedReceivedMode(mode);
      const cands = getReceivedStatusCandidates(mode);
      includeFlag = cands.includes(orderStatusToNumber(orderReceiptStatus));
      if (!includeFlag) setOrderReceiptStatus(""); // 含んでいないので、受注ステータスはデフォルトに戻す
      params = createGetListApiParams(mode, !includeFlag);
    } else {
      params = createGetListApiParams(mode);
    }
    setIsAllSelected(false); // 全選択を解除
    await get(params);
  };

  const handleCloseOrderConfirm = () => setIsPlaceOrder(false);

  const resetShippingModalValues = () => {
    setShippingCompanyName("");
    setShippingDate("");
    setExpectedShippingDate("");
    setArrivalDate("");
    setTrackingNumber("");
    setShippingStatus("");
  };

  // オーダー確定
  const handleUpdateOrderConfirm = async (editedOrderList: OrderSupplierCsv[]) => {
    const oList = orderList.filter((o) => o.selected).map(o => {
      const editedOrder = editedOrderList.find(eo => eo.id === o.id);
      o.shippingCount = editedOrder && typeof editedOrder.shippingCount === 'number' ? editedOrder.shippingCount : o.shippingCount;
      return o;
    });
    console.log('出荷する注文リスト：', oList);

    setIsUpdateProcessing(true);
    try {
      const orderApis: OrderSupplierUpdateParams["shipment_infos"] = oList.map((o) => ({
        order_no: o.orderNumber,
        order_date: o.orderDate,
        item_cd: o.productCd,
        jan_cd: o.janCd,
        shipping_quantity: o.shippingCount,
        shipping_status: shippingStatusStrToNumber(o.changedShippingStatus),
        ship_date: o.changedShippingDate,
        scheduled_date: o.changedExpectedShippingDate,
        arrival_date: o.changedArrivalDate,
        shipping_company: o.changedShippingCompanyName,
        shipping_no: o.changedTrackingNumber,
      }));
      const removedOrderObjApi = orderApis.map((oa) =>
        removeEmptyValues(oa, [
          "order_no",
          "arrival_date",
          "scheduled_date",
          "ship_date",
          "shipping_company",
          "shipping_no",
        ]),
      );
      await updateOrderSupplierApi({ shipment_infos: removedOrderObjApi });
      showAlert("出荷しました。");
      setIsPlaceOrder(false);
    } catch (err) {
      console.error(err);
      showAlert("出荷に失敗しました。", "error");
      const error2 = err as AxiosError;
      // レスポンス自体がなくエラーになった時
      if (!error2.response) {
        Notify.setNotification({
          show: true,
          title: "APIに接続できませんでした。",
          confirmLabel: "OK",
          actionsPosition: "end",
        });
        return;
      }
      const { status } = error2.response;
      const data = error2.response.data as {errors: string}[];
      switch (status) {
        // case 400:
        //   Notify.setNotification({
        //     show: true,
        //     title: "パラメータエラーです。",
        //     confirmLabel: "OK",
        //     actionsPosition: "end",
        //   });
        //   break;
        case 422:
          Notify.setNotification({
            // show: true,
            // title: data.messages.join('\n'),
            // confirmLabel: "OK",
            // actionsPosition: "end",
            show: true,
            title: "入力エラーがあります。",
            message: data.slice(0,10).map(e => e.errors).join('\n'),
            titleColor: "var(--error)",
          });
          break;
        default:
          break;
      }
      setIsPlaceOrder(false);
    } finally {
      setIsUpdateProcessing(false);
    }

    // 取得し直し
    await get(createGetListApiParams(selectedReceivedMode));
  };

  const settingUpdateValidation = (): string | undefined => {
    // if (shippingCompanyName === "") return "配送社情報は必須です。";
    // 送り状番号が同じで、かつ、発送先名が全て同じではない場合、エラーとする。
    if (
      !(
        trackingNumber === "" ||
        (trackingNumber !== "" &&
          orderList.filter(o => o.selected).reduce(
            (acc, cur) => acc && orderList[0].destinationName === cur.destinationName,
            true,
          ))
      )
    ) {
      return "送り状番号を指定する場合、選択した受注の発送先名が同一である必要があります。";
    }
    if (shippingStatus === "") return "「出荷/入荷待ち/欠品」を選択してください。"
    if (shippingDateError) return shippingDateError;
    if (expectedShippingDateError) return expectedShippingDateError;
    return undefined;
  };

  // 出荷情報設定更新
  const handleUpdateShipping = async () => {
    // バリデーションチェック
    const errMsg = settingUpdateValidation();
    if (errMsg) {
      showAlert(errMsg, "error");
      return;
    }

    const oList: OrderSupplier[] = structuredClone(orderList);
    const newList: OrderSupplier[] = oList.map((o) => {
      if (o.selected) {
        o.changedShippingDate = shippingDate;
        o.changedExpectedShippingDate = expectedShippingDate;
        o.changedArrivalDate = arrivalDate;
        o.changedShippingCompanyName = shippingCompanyName;
        o.changedTrackingNumber = trackingNumber;
        o.changedShippingStatus = shippingStatus;
      }
      return o;
    });
    setOrderList(newList);
    // showAlert("出荷情報設定しました。");
    setIsOpenShipping(false);
    setIsPlaceOrder(true);
    resetShippingModalValues();
  };

  // 出荷情報設定モーダルを閉じる
  const closeShipping = () => {
    setIsPlaceOrder(false);
    setIsOpenShipping(false);
    resetShippingModalValues();
  };

  const handleBack = () => navigate(-1);
  const tableHeadElement = () => (
    <>
      <TableRow>
        <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={2}>
          <Checkbox
            color="secondary"
            checked={isAllSelected}
            onChange={(_, check) => {
              const flag = orderList.every((o) => o.selected);
              setIsAllSelected(!flag);
              setOrderList((prev) => {
                const newOrders = structuredClone(prev);
                newOrders.forEach((o: OrderSupplier) => {
                  o.selected = !flag;
                });
                return newOrders;
              });
            }}
            inputProps={{ "aria-label": "customer-selected" }}
          />
          <Box>{`\n\n全選択`}</Box>
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>ステータス</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>CSV出力済</TableHeaderCellNarrow>
        <TableHeaderCellWide sx={styles.supplierTopCol}>注文番号</TableHeaderCellWide>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>商品コード</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>JANコード</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>ブランド</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>商品名</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>サイズ</TableHeaderCellNarrow>
        <TableHeaderCellWide sx={styles.supplierTopCol}>品番</TableHeaderCellWide>
        <TableHeaderCellWide right sx={styles.supplierTopCol}>
          受注数
        </TableHeaderCellWide>
        <TableHeaderCellWide right sx={styles.supplierTopCol}>
          金額
          {/* <br />
          (単価) */}
        </TableHeaderCellWide>
      </TableRow>
      <TableRow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>発送先名</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>注文日</TableHeaderCellNarrow>
        <TableHeaderCellWide sx={styles.supplierBottomCol}>納品希望日</TableHeaderCellWide>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
          仕入先
          <br />
          出荷日
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
          仕入先
          <br />
          出荷予定日
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
          仕入先
          <br />
          入荷予定
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>配送社名</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>送り状番号</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>出荷数</TableHeaderCellNarrow>
        <TableHeaderCellWide sx={styles.supplierBottomCol} colSpan={2}>
          店舗備考
        </TableHeaderCellWide>
      </TableRow>
    </>
  );
  const tableBodyElement = () => (
    <>
      {orderList.map((order, index) => {
        const isZebraStripeRow = index % 2 === 1;
        return (
          <Fragment key={order.id}>
            <TableRow>
              <TableCellNarrow center gray={isZebraStripeRow} rowSpan={2}>
                <Checkbox // 選択のチェックボックス
                  color="secondary"
                  checked={order.selected}
                  onChange={(_, check) => {
                    setOrderList((prev) => {
                      const newOrder = structuredClone(prev);
                      newOrder[index].selected = check;
                      return newOrder;
                    });
                  }}
                  inputProps={{ "aria-label": "supplier-selected" }}
                />
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.orderStatus}
              </TableCellNarrow>
              <TableCellNarrow center multi gray={isZebraStripeRow}>
                {order.csvDownloaded ? (
                  <Typography component="span" fontSize={18}>
                    済
                  </Typography>
                ) : (
                  <Typography
                    component="span"
                    fontSize={18}
                    sx={{ backgroundColor: "orange", fontWeight: "bold" }}
                  >
                    未
                  </Typography>
                )}
              </TableCellNarrow>
              <TableCellWide multi gray={isZebraStripeRow}>
                {order.orderNumber}
              </TableCellWide>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.productCd}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.janCd}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.brand}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.productName}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.size}
              </TableCellNarrow>
              <TableCellWide multi gray={isZebraStripeRow}>
                {order.productNumber}
              </TableCellWide>
              <TableCellWide right multi gray={isZebraStripeRow}>
                {localeStringDisZero(order.orderCount)}
              </TableCellWide>
              <TableCellWide right multi gray={isZebraStripeRow}>
                {/* {localeStringDisZero(order.headquartersCost)}
                <br /> */}
                {`${localeStringDisZero(order.headquartersUnitCost)}`}
              </TableCellWide>
            </TableRow>
            <TableRow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.destinationName}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.orderDate}
              </TableCellNarrow>
              <TableCellWide multi gray={isZebraStripeRow}>
                {order.deliveryDate}
              </TableCellWide>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.shippingDate}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.expectedShippingDate}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.arrivalDate}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.shippingCompanyName}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.trackingNumber}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.shippingCount}
              </TableCellNarrow>
              <TableCellWide multi gray={isZebraStripeRow} colSpan={2}>
                {order.shopRemarks}
              </TableCellWide>
            </TableRow>
          </Fragment>
        );
      })}
    </>
  );

  // スクロール処理
  const handleScroll = async (e: UIEvent<HTMLDivElement>) => {
    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading.current || isMoreLoading) {
      return;
    }
    // const scrollTop = window.scrollY;
    // const windowHeight = window.innerHeight;
    // const documentHeight = document.documentElement.scrollHeight;
    const { scrollTop } = e.currentTarget;
    const windowHeight = e.currentTarget.clientHeight;
    const documentHeight = e.currentTarget.scrollHeight;
    if (scrollTop + windowHeight + 10 >= documentHeight) {
      console.log("scroll event:");
      isFastLoading.current = true;
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  // const checkScrollable = () => document.documentElement.scrollHeight > window.innerHeight;

  // 中のコンテンツが小さいとスクロールできず、無限ローディングさせることができないため、事前に+αで読んでおく。
  // useEffect(() => {
  //   const oLength = orderList.length;
  //   if (limit <= oLength && oLength < total && !isLoading && !isFastLoading.current) {
  //     if (!checkScrollable()) {
  //       (async() => {
  //         await getMoreOrderList(createGetListApiParams(selectedReceivedMode));
  //       })();
  //     }
  //   }
  // }, [orderList, isLoading]);

  // useEffect(() => {
  //   (async () => {
  //     setInit(true);
  //   })();
  //   window.addEventListener('scroll', handleScroll);
  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //   }
  // }, []);

  useEffect(() => {
    setTimeout(() => {
      (async () => {
        if (!localEnv && !(localStorage.getItem("supplier_cd") || localStorage.getItem("warehouse_cd"))) {
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: "仕入先または倉庫コードが設定されていません。再度ログインしてください。",
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else if (!localEnv && checkAccessTokenExpired()) {
          // 期限切れなら
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        }
      })();
    }, 0);
  }, []);

  // ローディングの統一
  useEffect(() => {
    setIsLoading(isOrderLoading || isOptionsLoading);
  }, [isOrderLoading, isOptionsLoading]);

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      await getMoreOrderList(createGetListApiParams(selectedReceivedMode));
    })();
  }, [fireMoreLoadEvent]);

  // 初期表示処理
  useEffect(() => {
    (async () => {
      await get(createGetListApiParams(selectedReceivedMode));
      setInit(true);
    })();
  }, []);

  const checkOrderSelected = () => orderList.every((o) => !o.selected);

  return (
    <>
      <OrderContainer
        orderTitle="受注商品一覧"
        OrderTable={
          <OrderTable
            searchLen={total}
            isAllLoaded={orderList.length >= total}
            tableHeadElement={() => tableHeadElement()}
            tableBodyElement={() => tableBodyElement()}
            isLoading={isLoading}
            isMoreLoading={isMoreLoading}
            handleScroll={handleScroll}
            limit={limit}
            total={total}
            orderListLength={0}
            isFastLoading={isFastLoading}
            getMoreOrderList={getMoreOrderList}
            createGetListApiParams={() => createGetListApiParams(selectedReceivedMode)}
          />
        }
        HeaderBox={HeaderBox(
          selectedReceivedMode,
          handleSearch,
          handleBack,
          checkOrderSelected(),
          checkRefineSearch(),
          setIsOrderSearch,
          setIsOpenShipping,
          setIsUploadOrderSupplierCsv,
          setIsOpenDownload,
          setIsOpenEmailSetting,
          showAlert,
        )}
        isLoading={isLoading}
        helpPath={`/resources/orders/manual_order_${getUserType() === "C0004" ? "supplier" : "depot"}.pdf`}
      />
      {!isLoading && (
        <OrderSearchDialog
          open={isOrderSearch}
          onClose={() => setIsOrderSearch(false)}
          onSearch={handleSearch}
          isRepresentativeCd={isRepresentativeCd}
          isCustomerOrder={isCustomerOrder}
          shopName={shopName}
          orderStatus={orderStatus}
          orderReceiptStatus={orderReceiptStatus}
          storeOrderer={storeOrderer}
          approver={approver}
          orderStartDate={orderStartDate}
          orderEndDate={orderEndDate}
          companyCd={companyCdField}
          companyName={companyName}
          centerCd={centerCd}
          orderNumber={orderNumber}
          productCd={productCd}
          productName={productName}
          brand={brand}
          janCd={janCd}
          productNumber={productNumber}
          tyreWidth={tyreWidth}
          tyreRatio={tyreRatio}
          tyreRimDiam={tyreRimDiam}
          wheelWidth={wheelWidth}
          wheelPcd={wheelPcd}
          wheelHole={wheelHole}
          wheelRimDiam={wheelRimDiam}
          classification={classification}
          supplier={supplier}
          tyreOptions={tyreOptions}
          wheelOptions={wheelOptions}
          classificationOptions={classificationOptions}
          brandOptions={brandOptions}
          selectedReceivedMode={selectedReceivedMode}
          setIsRepresentativeCd={setIsRepresentativeCd}
          setIsCustomerOrder={setIsCustomerOrder}
          setShopName={setShopName}
          setOrderStatus={setOrderStatus}
          setOrderReceiptStatus={setOrderReceiptStatus}
          setStoreOrderer={setStoreOrderer}
          setApprover={setApprover}
          setOrderStartDate={setOrderStartDate}
          setOrderEndDate={setOrderEndDate}
          setCompanyCd={setCompanyCdField}
          setCompanyName={setCompanyName}
          setCenterCd={setCenterCd}
          setProductCd={setProductCd}
          setOrderNumber={setOrderNumber}
          setProductName={setProductName}
          setBrand={setBrand}
          setjanCd={setjanCd}
          setProductNumber={setProductNumber}
          setTyreWidth={setTyreWidth}
          setTyreRatio={setTyreRatio}
          setTyreRimDiam={setTyreRimDiam}
          setWheelWidth={setWheelWidth}
          setWheelPcd={setWheelPcd}
          setWheelHole={setWheelHole}
          setWheelRimDiam={setWheelRimDiam}
          setClassification={setClassification}
          setSupplier={setSupplier}
          setCategory={setCategory}
          screenType="仕入先・外部倉庫"
        />
      )}
      <CsvModals
        refresh={() => get(createGetListApiParams(selectedReceivedMode))}
        isOpenOrderSupplierCsvUpload={isUploadOrderSupplierCsv}
        setIsOpenOrderSupplierCsvUpload={setIsUploadOrderSupplierCsv}
      />
      <OrderSupplierConfirmDialog
        open={isPlaceOrder}
        onClose={() => handleCloseOrderConfirm()}
        onUpdate={(editedOrderList: OrderSupplierCsv[]) => handleUpdateOrderConfirm(editedOrderList)}
        selectedOrderList={orderList.filter((o) => o.selected)}
        isUpdateProcessing={isUpdateProcessing}
      />
      {/* 出荷情報設定モーダル */}
      <ShipInfo
        open={isOpenShipping}
        onClose={closeShipping}
        onUpdate={handleUpdateShipping}
        shippingCompanyName={shippingCompanyName}
        trackingNumber={trackingNumber}
        shippingDate={shippingDate}
        expectedShippingDate={expectedShippingDate}
        shippingStatus={shippingStatus}
        arrivalDate={arrivalDate}
        setShippingCompanyName={setShippingCompanyName}
        setTrackingNumber={setTrackingNumber}
        setShippingDate={setShippingDate}
        setExpectedShippingDate={setExpectedShippingDate}
        setShippingStatus={setShippingStatus}
        setArrivalDate={setArrivalDate}
        setShippingDateError={setShippingDateError}
        setExpectedShippingDateError={setExpectedShippingDateError}
      />
      {/* 受注ダウンロード選択モーダル */}
      <SelectDownloadMode open={isOpenDownload} onClose={async () => {
        setIsOpenDownload(false);
        // 取得し直し
        await get(createGetListApiParams(selectedReceivedMode));
      }} dlMode={selectedReceivedMode} />
      <ReceiveEmailSetting open={isOpenEmailSetting} onClose={() => setIsOpenEmailSetting(false)} />
    </>
  );
}
